import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgFor } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { AlertBlockComponent } from '../alert-block/alert-block.component';
import { CopyToClipboardDirective } from '../directives/button.directive';
import { E2eIdDirective } from '../directives/e2e-id.directive';

/**
 * Presentational show-once recovery-codes panel (SD-8082): the codes themselves, the copy /
 * download utilities and the "I have saved these" acknowledgement.
 *
 * Deliberately headerless and footerless so it can be embedded by either host without a second
 * dialog surface: inline as the final stage of the 2FA setup steps (enrollment), and inside
 * {@link RecoveryCodesDialogComponent} for the Settings regenerate path. The host owns the
 * heading and the action button, and gates that button on {@link acknowledgedChange}.
 */
@Component({
    selector: 'app-recovery-codes-panel',
    templateUrl: './recovery-codes-panel.component.html',
    styleUrls: ['./recovery-codes-panel.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgFor,
        MatCheckboxModule,
        MatDividerModule,
        AlertBlockComponent,
        CopyToClipboardDirective,
        E2eIdDirective,
    ],
})
export class RecoveryCodesPanelComponent
{
    @Input() codes: string[] = [];

    /** Emits whenever the user toggles the acknowledgement, so the host can gate its action. */
    @Output() acknowledgedChange = new EventEmitter<boolean>();

    acknowledged = false;

    /** Newline-joined form, used for copy-all and the downloaded text file. */
    get codesBlock(): string {
        return this.codes.join('\n');
    }

    /** trackBy for the codes list — codes are unique within a set. */
    trackByCode(_: number, code: string): string {
        return code;
    }

    onAcknowledgedChange(checked: boolean): void {
        this.acknowledged = checked;
        this.acknowledgedChange.emit(checked);
    }

    /** Download the codes as a plain-text file so the user has an offline copy. */
    download(): void {
        const blob = new Blob([this.codesBlock + '\n'], {type: 'text/plain'});
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'payhoa-recovery-codes.txt';
        // A programmatic click on a detached anchor is unreliable in Firefox — attach, click, detach.
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    }
}
